import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Award, ShieldCheck } from 'lucide-react';
import { navigationLinks, contactInfo } from '../data';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-white text-stone-600 pt-16 pb-8 border-t border-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-3 group">
              <img 
                src="https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/stenbelaegning-logo-2.png" 
                alt="Stenbelægning.dk logo" 
                className="h-14 sm:h-16 lg:h-18 w-auto object-contain shrink-0 group-hover:opacity-90 transition-opacity bg-transparent" 
              />
              <span className="sr-only">
                Stenbelægning.dk
              </span>
            </Link>
            <p className="text-sm text-stone-500 leading-relaxed">
              Vi udfører eksklusiv, helstøbt stenbelægning til indkørsler, terrasser, trapper og gangarealer i hele Danmark. 
            </p>
            <div className="flex items-center gap-2 text-stone-900 text-sm font-semibold">
              <ShieldCheck className="h-5 w-5 text-stone-900" />
              <span>{contactInfo.guarantee}</span>
            </div>
          </div>

          {/* Navigation Links */}
          <div>
            <h3 className="text-stone-900 font-semibold text-base mb-4 tracking-wider uppercase text-xs">Hurtige links</h3>
            <ul className="space-y-2.5 text-sm">
              {navigationLinks.reduce((acc, link) => {
                if (link.children) {
                  return [...acc, ...link.children];
                }
                if (link.path !== '#') {
                  return [...acc, link];
                }
                return acc;
              }, []).map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="hover:text-emerald-600 transition-colors block group"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-stone-900 font-semibold text-base mb-4 tracking-wider uppercase text-xs">Kontakt os</h3>
            <ul className="space-y-3.5 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-stone-900 shrink-0 mt-0.5" />
                <span>{contactInfo.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-stone-900 shrink-0" />
                <a href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`} className="hover:text-emerald-600 transition-colors">
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-stone-900 shrink-0" />
                <a href={`mailto:${contactInfo.email}`} className="hover:text-emerald-600 transition-colors break-all">
                  {contactInfo.email}
                </a>
              </li>
              {contactInfo.cvr && (
                <li className="text-xs text-stone-400 pt-1">
                  CVR: {contactInfo.cvr}
                </li>
              )}
            </ul>
          </div>

          {/* Opening Hours */}
          <div>
            <h3 className="text-stone-900 font-semibold text-base mb-4 tracking-wider uppercase text-xs">Åbningstider</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-stone-900 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-stone-900">Kontor & rådgivning</p>
                  <p className="text-stone-600 mt-0.5">{contactInfo.hours}</p>
                </div>
              </div>
              <div className="bg-stone-50 p-3 rounded-lg border border-stone-100 text-xs text-stone-500">
                <p className="font-semibold text-stone-700 mb-1">Brug for rådgivning?</p>
                <p>Ring eller skriv til os for at aftale et uforpligtende besøg på din adresse.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-stone-100 text-center text-xs text-stone-400 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p>© {currentYear} Stenbelægning.dk. Alle rettigheder forbeholdes.</p>
          <div className="flex gap-6">
            <Link to="/kontakt" className="hover:underline">Privatlivspolitik</Link>
            <Link to="/service" className="hover:underline">Betingelser</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
